<script>import { trackComponent } from "@sentry/svelte";
trackComponent({ "trackInit": true, "trackUpdates": true, "componentName": "SelectMultiple" });
import "tailwindcss/tailwind.css";
import List from "./components/List.svelte";
import { Input } from "../../inputs/index.js";
import Icon from "../../elements/icon/Icon.svelte";
import { TagList } from "../index.js";
import { createEventDispatcher, onDestroy, onMount } from "svelte";
export let keys = [];
export let options = [];
export let name;
export let isDisabled = false;
export let minItems = 0;
export let maxItems = 1e3;
export let label = void 0;
export let hint = void 0;
export let placeholder = void 0;
export let hideTags = false;
export let hasSearch = true;
export let showToggle = false;
export let whitelist = [];
export let blacklist = [];
export let query = void 0;
export let showSortedList = true;
export let showList = false;
const getItems = (key) => options.filter((option) => option.parentKey === key);
const dispatch = createEventDispatcher();
let listComponentWrapper = null;
const getChildren = (key) => {
    const CHILDREN = [];
    for (const OPTION of options) {
        if (OPTION.parentKey === key) {
            CHILDREN.push(OPTION.key);
            CHILDREN.push(...getChildren(OPTION.key));
        }
    }
    return CHILDREN;
};
const formatTree = (branches, parents = []) => {
    const RESULTS = branches.filter((branch) => whitelist.length === 0 || whitelist.includes(branch.key)).map(({ key, label: label2, parentKey }) => ({
        children: [...getChildren(key)],
        items: formatTree(getItems(key), [...parents, key]),
        key,
        label: label2,
        parentKey,
        parents: [...parents, key]
    }));
    if (showSortedList) {
        RESULTS.sort((branchA, branchB) => branchA.label.localeCompare(branchB.label));
    }
    return RESULTS;
};
$: TREE = formatTree(options.filter((option) => option.parentKey === void 0), []);
const DISTANCE_TO_BOTTOM_THRESHOLD = 10;
const toggleList = () => {
    showList = !showList;
    dispatch(showList ? "open" : "close");
};
const clearQuery = () => {
    query = void 0;
};
const handleDocumentClick = (event) => {
    if (event.target === null) {
        return;
    }
    if (listComponentWrapper === null) {
        return;
    }
    if (!listComponentWrapper.contains(event.target)) {
        dispatch("close");
        showList = false;
        clearQuery();
    }
};
const onBottomScroll = () => {
    if (listComponentWrapper === null) {
        return;
    }
    const DISTANCE_TO_BOTTOM = listComponentWrapper.scrollHeight - listComponentWrapper.scrollTop - listComponentWrapper.clientHeight;
    if (DISTANCE_TO_BOTTOM <= DISTANCE_TO_BOTTOM_THRESHOLD) {
        dispatch("scrollThreshold");
    }
};
const onClick = () => {
    if (hasSearch) {
        return;
    }
    toggleList();
};
onMount(() => {
    document.addEventListener("click", handleDocumentClick, true);
});
onDestroy(() => {
    document.removeEventListener("click", handleDocumentClick, true);
});
const setValidation = (keys2, maxItems2, minItems2) => {
    const COUNT = Array.isArray(keys2) ? keys2.length : 0;
    if (maxItems2 !== void 0 && COUNT > maxItems2) {
        return "max_items";
    }
    if (minItems2 !== void 0 && COUNT < minItems2) {
        return "min_items";
    }
    return "";
};
const selectAll = () => {
    keys = TREE.flatMap((branch) => branch.key);
};
const deselectAll = () => {
    keys = [];
};
$: validation = setValidation(keys ?? [], maxItems, minItems);
</script>

<div
    class = 's8-select-multiple'
    class:s8-autocomplete = { hasSearch }
    class:s8-select = { !hasSearch }
    class:s8-has-label = { label !== undefined }
>

    {#if showToggle && keys !== undefined && keys.length > 0}
        <button on:click = { deselectAll } class = 's8-select-deselect-all'>
            Deselect All
        </button>
    {:else if showToggle && keys !== undefined && keys.length < TREE.length}
        <button on:click = { selectAll } class = 's8-select-deselect-all'>
            Select All
        </button>
    {/if}

    {#if Array.isArray(options) && Array.isArray(keys)}

        <div class = 'relative'>

            <Input
                bind:value = { query }
                isRequired = { false }
                isReadonly = { !hasSearch }
                on:click = { onClick }
                hasStyle = { hasSearch }
                { validation }
                { isDisabled }
                { label }
                { placeholder }
                { name }
                { hint }
                hasMinItems = { minItems !== undefined && minItems > 0 }
            >
                <svelte:fragment slot = 'afterInput'>
                    {#if !isDisabled}
                        {#if query === undefined}
                            <Icon
                                key = { showList ? 'chevronUp' : 'chevronDown' }
                                css = 's8-select-icon'
                                on:click = { toggleList }
                            />
                        {:else}
                            <Icon
                                key = 'close'
                                css = 's8-select-icon'
                                on:click = { clearQuery }
                            />
                        {/if}
                    {/if}
                </svelte:fragment>
            </Input>

            <div
                bind:this = { listComponentWrapper }
                on:scroll={ onBottomScroll }
                class = 's8-dropdown'
                class:s8-visible = { showList || query !== undefined }
            >
                <List
                    bind:keys
                    { query }
                    { blacklist }
                    { maxItems }
                    tree = { TREE }
                    { options }
                    { name }
                />
            </div>

        </div>

        {#if !hideTags}
            <TagList bind:keys mode = 'remove' { options }/>
        {/if}

    {/if}

</div>

<style>
    div.s8-select-multiple {
        position: relative;
        width: 100%;
        padding-bottom: 16px;
    }

        div.s8-select-multiple .s8-select-deselect-all {
            font-size: 8px;
            position: absolute;
            right: 0px;
            z-index: 10;
        }

        div.s8-select-multiple.s8-select :global(input) {
                caret-color: transparent;
                background: linear-gradient(180deg, rgba(39,39,17,0.06) 0%, rgba(39,39,17,0.12) 100%);
                padding-right: 30px !important;
                text-overflow: ellipsis;
            }

        div.s8-select-multiple.s8-select :global(input):not(:disabled):hover {
                    cursor: pointer !important;
                    background: linear-gradient(180deg, rgba(39,39,17,0.08) 0%, rgba(39,39,17,0.14) 100%);
                }

        div.s8-select-multiple.s8-select :global(input):not(:disabled):focus {
                    background: linear-gradient(180deg, rgba(68,131,95,0.12) 0%, rgba(68,131,95,0.24) 100%);
                }

        div.s8-select-multiple.s8-select :global(input):not(:disabled):focus::-moz-placeholder {
                        color: rgba(68,131,95,0.48) !important;
                    }

        div.s8-select-multiple.s8-select :global(input):not(:disabled):focus::placeholder {
                        color: rgba(68,131,95,0.48) !important;
                    }

        div.s8-select-multiple.s8-select :global(input.s8-touched:invalid:not(:focus)) {
                background: linear-gradient(180deg, rgba(239,35,59,0.12) 0%, rgba(239,35,59,0.24) 100%);
            }

        div.s8-select-multiple.s8-select :global(input.s8-touched:invalid:not(:focus))::-moz-placeholder {
                    color: rgba(239,35,59,0.48) !important;
                }

        div.s8-select-multiple.s8-select :global(input.s8-touched:invalid:not(:focus))::placeholder {
                    color: rgba(239,35,59,0.48) !important;
                }

        div.s8-select-multiple :global(svg.s8-select-icon) {
            cursor: pointer !important;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            height: 15px;
            right: 10px;
        }

        div.s8-select-multiple :global(div.s8-input) {
            margin-bottom: 0px !important;
        }

        div.s8-select-multiple :global(div.s8-footer) {
            min-height: auto !important;
        }

        div.s8-select-multiple :global(div.s8-tag-list) {
            margin-bottom: 0px !important;
        }

        div.s8-select-multiple div.s8-dropdown {
        z-index: 0;
        overflow-x: hidden;
        border-radius: 0.5rem;
        background-color: rgba(255, 255, 255, 1);
        padding: 0.5rem;
        --tw-shadow: 0px 2px 15px 0px rgba(39, 39, 17, 0.10);;
        --tw-shadow-colored: 0px 2px 15px 0px var(--tw-shadow-color);
        box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
        max-height: 0px;
        opacity: 0;
        transition-property: all;
        transition-duration: 200ms;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

            top: 40px;
            left: 0px;
            right: 0px;
            position: absolute;
}

        div.s8-select-multiple div.s8-dropdown.s8-visible {
                opacity: 1;
                max-height: 300px;
                z-index: 20;
            }

        div.s8-select-multiple div.s8-dropdown.s8-visible :global(ul) {
                    display: block;
                }

        div.s8-select-multiple div.s8-dropdown:not(.s8-visible) :global(ul) {
                    display: none;
                }

        div.s8-select-multiple.s8-has-label div.s8-dropdown {
            top: 50px;
        }</style>